import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Form } from '@nucleus-commerce/ui/components/form';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromAddress, toAddressInput } from '../api/mappers';
import { useUpdateOrder } from '../hooks/use-order-mutations';
import {
  ADDRESS_FIELDS,
  addressSchema,
  type AddressFormInput,
  type AddressFormOutput,
} from '../schemas/address-schema';
import { orderConflictKey } from '../schemas/order-rules';

import { AddressFields } from './address-fields';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(ADDRESS_FIELDS);

/** `body.shippingAddress.city` lands on `city`. */
const mapField = (field: string) => field.replace(/^shippingAddress\./, '');

export interface EditAddressDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Shipping address while unfulfilled; a 409 means fulfilment started meanwhile. */
export function EditAddressDialog({ storeId, order, open, onOpenChange }: EditAddressDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const update = useUpdateOrder(storeId, 'orders:toasts.addressUpdated');
  const form = useForm<AddressFormInput, unknown, AddressFormOutput>({
    resolver: useI18nResolver(addressSchema),
    defaultValues: fromAddress(order.shippingAddress),
  });

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync({
        id: order.id,
        body: { shippingAddress: toAddressInput(values) },
      });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      const conflict = orderConflictKey(error, 'orders:editAddress.errors.locked');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:editAddress.title')}</DialogTitle>
          <DialogDescription>{t('orders:editAddress.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <AddressFields control={form.control} />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? t('common:actions.saving') : t('orders:editAddress.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
